import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { X, FolderOpen, Folder, Loader2, Search as SearchIcon, FileText, FileSpreadsheet, FileImage, File as FileIcon, ExternalLink, ChevronRight, FolderPlus, Upload, Trash2, Home, Pencil } from "lucide-react";
import { isImageFile, imgSrc, ImageLightbox } from "../common/FileViewer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const OFFICE = /\.(docx?|xlsx?|pptx?)$/i;

function prettyBytes(n) {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function fileHref(f) {
  const full = `${BACKEND}${f.url}`;
  if (OFFICE.test(f.name || "")) return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(full)}`;
  return full;
}
function FileTypeIcon({ ct, name }) {
  const c = `${ct || ""} ${name || ""}`.toLowerCase();
  if (c.includes("image") || /\.(png|jpe?g|webp|gif|bmp|heic)$/.test(c)) return <FileImage className="h-4 w-4 text-sky-500" />;
  if (c.includes("sheet") || c.includes("excel") || /\.(xlsx?|csv|numbers)$/.test(c)) return <FileSpreadsheet className="h-4 w-4 text-emerald-600" />;
  if (c.includes("pdf") || c.includes("word") || c.includes("text") || /\.(pdf|docx?|txt|rtf)$/.test(c)) return <FileText className="h-4 w-4 text-rose-500" />;
  return <FileIcon className="h-4 w-4 text-slate-400" />;
}

export default function ClientFolders({ clientId, clientName, onClose }) {
  const [subpath, setSubpath] = useState("");
  const [subfolders, setSubfolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileQ, setFileQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const loadLevel = async (sp) => {
    setLoading(true);
    try {
      const d = await api("GET", `/clients/${clientId}/folders?subpath=${encodeURIComponent(sp || "")}`);
      setSubfolders(d.subfolders || []);
      setFiles(d.files || []);
    } catch { toast.error("Could not load files"); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadLevel(""); /* eslint-disable-next-line */ }, [clientId]);

  const drill = (seg) => { const sp = subpath ? `${subpath}/${seg}` : seg; setSubpath(sp); loadLevel(sp); };
  const goCrumb = (sp) => { setSubpath(sp); loadLevel(sp); };

  const createSubfolder = async () => {
    const name = (window.prompt("New sub-folder name:") || "").trim().replace(/\//g, "-");
    if (!name) return;
    const sp = subpath ? `${subpath}/${name}` : name;
    setBusy(true);
    try { await api("POST", `/clients/${clientId}/folders/subfolder`, { subpath: sp }); toast.success(`Created sub-folder "${name}"`); await loadLevel(subpath); }
    catch (e) { toast.error(e.message || "Could not create sub-folder"); }
    finally { setBusy(false); }
  };

  const uploadDocs = async (fileList) => {
    if (!fileList?.length) return;
    setBusy(true);
    let ok = 0;
    for (const file of Array.from(fileList)) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("subpath", subpath || "");
      try {
        const res = await fetch(`${BACKEND}/api/clients/${clientId}/files`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
        if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Upload failed"); }
        ok++;
      } catch (e) { toast.error(`${file.name}: ${e.message}`); }
    }
    if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
    setBusy(false);
    await loadLevel(subpath);
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete "${f.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api("DELETE", `/client-files/${f.id}`); toast.success("Deleted"); setFiles((prev) => prev.filter((x) => x.id !== f.id)); }
    catch (e) { toast.error(e.message || "Could not delete"); }
    finally { setBusy(false); }
  };

  const renameFile = async (f) => {
    const nn = (window.prompt("Rename file to:", f.name) || "").trim();
    if (!nn || nn === f.name) return;
    setBusy(true);
    try { await api("PATCH", `/client-files/${f.id}`, { name: nn }); toast.success("Renamed"); await loadLevel(subpath); }
    catch (e) { toast.error(e.message || "Could not rename"); }
    finally { setBusy(false); }
  };

  const shownFiles = useMemo(() => {
    const ql = fileQ.trim().toLowerCase();
    return (ql ? files.filter((f) => (f.name || "").toLowerCase().includes(ql)) : files);
  }, [files, fileQ]);
  const crumbs = useMemo(() => (subpath ? subpath.split("/") : []), [subpath]);
  const imageFiles = useMemo(() => shownFiles.filter(isImageFile), [shownFiles]);
  const openImg = (f) => { const idx = imageFiles.findIndex((x) => x.id === f.id); setLightbox({ images: imageFiles, index: Math.max(0, idx) }); };

  return (
    <div className="fixed inset-0 z-[70] bg-slate-900/55 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6" data-testid="overlay-client-folders">
      <div className="relative w-full max-w-4xl my-2 rounded-lg overflow-hidden shadow-2xl border border-sky-300 bg-gradient-to-b from-sky-100 to-sky-200 flex flex-col max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3.5rem)]">
        <div className="flex items-center justify-between bg-[#2a9fd6] text-white px-3 py-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold"><FolderOpen className="h-4 w-4" /> Client's File Bank</div>
          <button onClick={onClose} data-testid="client-folders-close-x" title="Close" className="h-6 w-6 rounded-sm hover:bg-white/20 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-4 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="h-10 w-10 rounded-full bg-white shadow flex items-center justify-center shrink-0"><FolderOpen className="h-5 w-5 text-sky-500" /></div>
            <div>
              <h2 className="text-xl font-display font-bold text-slate-700 leading-tight">{clientName}</h2>
              <p className="text-[11px] text-slate-500">Files &amp; folders for this client — drop in photos, PDFs, quotes and docs.</p>
            </div>
          </div>

          <div className="relative rounded-sm border border-slate-300 bg-white/80 min-h-[20rem] flex-1 flex flex-col"
            onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOver(false); }}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadDocs(e.dataTransfer.files); }}>
            {dragOver && <div className="absolute inset-0 z-20 bg-sky-500/10 border-2 border-dashed border-sky-500 rounded-sm flex items-center justify-center pointer-events-none" data-testid="cf-dropzone"><span className="text-sky-700 font-black text-sm bg-white/95 px-4 py-2 rounded-lg shadow">Drop file or image to upload</span></div>}
            <div className="flex items-center gap-2 p-2 border-b border-slate-200 flex-wrap shrink-0" data-testid="cf-toolbar">
              <div className="flex items-center gap-1 flex-1 min-w-0 text-[13px] font-bold text-slate-800" data-testid="cf-breadcrumb">
                <button onClick={() => goCrumb("")} className="inline-flex items-center gap-1 hover:text-sky-600 truncate"><Home className="h-3.5 w-3.5" /> Home</button>
                {crumbs.map((c, i) => (
                  <span key={i} className="inline-flex items-center gap-1 min-w-0">
                    <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <button onClick={() => goCrumb(crumbs.slice(0, i + 1).join("/"))} className="hover:text-sky-600 truncate">{c}</button>
                  </span>
                ))}
              </div>
              <button onClick={createSubfolder} disabled={busy} data-testid="cf-new-subfolder-btn"
                className="inline-flex items-center gap-1 rounded-sm border border-sky-400 bg-sky-50 text-sky-700 text-[12px] font-semibold px-2 py-1.5 hover:bg-sky-100 disabled:opacity-50"><FolderPlus className="h-3.5 w-3.5" /> Sub-folder</button>
              <input ref={fileInputRef} type="file" multiple className="hidden" data-testid="cf-upload-input"
                onChange={(e) => { uploadDocs(e.target.files); e.target.value = ""; }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={busy} data-testid="cf-upload-btn"
                className="inline-flex items-center gap-1 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold px-2.5 py-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload</button>
              <div className="relative">
                <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input value={fileQ} onChange={(e) => setFileQ(e.target.value)} placeholder="Filter…" data-testid="cf-file-search"
                  className="w-32 rounded-sm border border-slate-300 bg-white pl-7 pr-2 py-1 text-[12px]" />
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="cf-scroll">
              {loading ? (
                <div className="h-40 flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {subfolders.map((sf) => (
                    <li key={`d-${sf.name}`} data-testid={`cf-subfolder-${sf.name}`}>
                      <button onClick={() => drill(sf.name)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-sky-50">
                        <Folder className="h-4 w-4 text-amber-500 shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-[13px] font-semibold text-slate-800">{sf.name}</span>
                        <span className="text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{sf.count}</span>
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      </button>
                    </li>
                  ))}
                  {shownFiles.map((f) => (
                    <li key={f.id} data-testid={`cf-file-${f.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                      {isImageFile(f)
                        ? <button onClick={() => openImg(f)} data-testid={`cf-thumb-${f.id}`} className="shrink-0 rounded overflow-hidden border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-sky-300"><img src={imgSrc(f)} alt={f.name} loading="lazy" className="h-14 w-14 object-cover" /></button>
                        : <FileTypeIcon ct={f.content_type} name={f.name} />}
                      <button onClick={() => (isImageFile(f) ? openImg(f) : window.open(fileHref(f), "_blank"))} className="flex-1 min-w-0 truncate text-left text-[13px] text-slate-800 hover:text-sky-700" title={f.name}>{f.name}</button>
                      <span className="text-[11px] text-slate-400 shrink-0">{prettyBytes(f.size)}</span>
                      {isImageFile(f)
                        ? <button onClick={() => openImg(f)} data-testid={`cf-open-${f.id}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50 shrink-0">View <ExternalLink className="h-3 w-3" /></button>
                        : <a href={fileHref(f)} target="_blank" rel="noreferrer" data-testid={`cf-open-${f.id}`} className="inline-flex items-center gap-1 rounded-sm border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-sky-700 hover:bg-sky-50 shrink-0">Open <ExternalLink className="h-3 w-3" /></a>}
                      <button onClick={() => renameFile(f)} disabled={busy} data-testid={`cf-rename-${f.id}`} title="Rename"
                        className="rounded-sm border border-slate-300 bg-white p-1 text-slate-600 hover:bg-slate-50 shrink-0 disabled:opacity-50"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => deleteFile(f)} disabled={busy} data-testid={`cf-delete-${f.id}`} title="Delete"
                        className="rounded-sm border border-slate-300 bg-white p-1 text-rose-600 hover:bg-rose-50 shrink-0 disabled:opacity-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </li>
                  ))}
                  {subfolders.length === 0 && shownFiles.length === 0 && <li className="p-8 text-center text-slate-400 text-sm">This folder is empty.</li>}
                </ul>
              )}
            </div>
          </div>

          <button onClick={onClose} data-testid="client-folders-close" className="mt-4 w-full rounded-md bg-slate-900 hover:bg-slate-700 text-white font-bold py-2.5">Close</button>
        </div>
      </div>
      {lightbox && <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </div>
  );
}
